import { cloneDeep, isEqual, sortBy } from 'lodash-es';
import {
  LOGICAL_OPERATORS,
  TOKEN_STATUS,
} from '@/common/components/molecules/filterSearch/filterSearch.define';
import { ref, nextTick, onMounted, computed, watch, onBeforeUnmount, inject } from 'vue';
import {
  EditingFilterToken,
  FilterDropdownInfoCategories,
  FilterDropdownItem,
  FilterHistory,
  FilterItem,
  FilterSelectedValues,
  FilterToken,
  FILTER_SEARCH_REGISTRY_KEY,
} from '@/common/components/molecules/filterSearch/filterSearch.type';
import { useInternational } from '@/common/locale';
import { useFilterPersistence } from './composables/useFilterPersistence';
import { useFilterDropdown } from './composables/useFilterDropdown';
import { useFilterLayout } from './composables/useFilterLayout';
import { useFilterHistory } from './composables/useFilterHistory';
import { useFilterTokens } from './composables/useFilterTokens';
import { useFilterKeyboard } from './composables/useFilterKeyboard';
import { useFilterEnvStore } from './composables/useFilterEnvStore';
import { useRoute } from 'vue-router';
import type { StoreDefinition } from 'pinia';
import { useFilterActiveSessionStore } from '@/common/stores/filter-active-session';
import type { SlideTabPanelInfo } from '@/common/stores/slide-detail';

type SlideStoreLike = {
  selectedSlideTabPanel?: SlideTabPanelInfo;
};

export interface Props {
  storageKey: string;
  filterItems: FilterItem[];
  selectedValues?: FilterSelectedValues;
  placeholder?: string;
  useLogFilter?: string;
  autoValueItems?: Record<string, { id: string; name: string }[]>;
  useDashboardStore?: boolean;
  defaultActive?: boolean;
  persistActiveState?: boolean;
}

export interface Emit {
  (e: 'complete', result: FilterSelectedValues): void;
  (e: 'update:selectedValues', result: FilterSelectedValues): void;
}

export const setup = (props: Props, emit: Emit) => {
  const { t } = useInternational();
  const route = useRoute();

  const { getEnv, saveEnv } = useFilterEnvStore(props.useDashboardStore);

  // 슬라이드 디테일 내부에서 사용되는 경우, 슬라이드 스택 세션 동안 ON/OFF 상태 유지
  const slideStoreDef = inject<StoreDefinition | null>('slideDetailStore', null);
  const slideStore = (slideStoreDef ? slideStoreDef() : null) as SlideStoreLike | null;
  const filterActiveSessionStore = useFilterActiveSessionStore();

  const getCurrentSlideValue = (): number | null => {
    const value = slideStore?.selectedSlideTabPanel?.value;
    return typeof value === 'number' ? value : null;
  };

  const filterItems = ref<FilterItem[]>(cloneDeep(props.filterItems ?? []));
  const tokenList = ref<FilterToken[]>([]);
  const lastToken = computed<FilterToken>(() => tokenList.value[tokenList.value.length - 1]);
  const hasCompletedToken = computed<boolean>(() =>
    tokenList.value.some(
      ({ status }) => status === TOKEN_STATUS.OPERATOR_DONE || status === TOKEN_STATUS.VALUE_DONE,
    ),
  );
  const editingToken = ref<EditingFilterToken | null>(null);
  const selectedLogicalOperator = ref('OR');
  const pinnedTokenList = ref<FilterSelectedValues | undefined>(undefined);
  const isFilterActive = ref<boolean>(props.defaultActive ?? false);
  const filterHistory = ref<FilterHistory[]>([]);

  // filterItems 변경 시 removeTokens → loadEnvData → setTokenListBySelectedResult 구간에서
  // tokenList watcher가 빈 데이터를 ENV에 저장하는 race condition을 방지하는 guard
  const isSuppressingSave = ref(false);

  // createDraftToken 등 VALUE_DONE 결과에 영향 없는 tokenList 변경 시 중복 저장 방지
  const lastSavedFilterResult = ref<FilterSelectedValues | null>(null);

  // filterItems watcher가 async이므로, 빠른 연속 호출 시 이전 invocation의 post-await 로직을
  // 취소하기 위한 버전 카운터 (stale invocation guard)
  let filterItemsWatcherVersion = 0;

  // emitComplete는 tokens 초기화 이후에 정의되므로, 런타임에만 호출되는 레이트 바인딩 패턴 사용
  let emitComplete: () => void = () => {};
  const getEmitComplete = () => emitComplete;

  // Persistence
  const {
    saveFilterSelectedValues,
    removeFilterSelectedValues,
    saveFilterActiveState,
    saveHistoryToEnv,
    loadEnvData,
  } = useFilterPersistence({
    storageKey: props.storageKey,
    getEnv,
    saveEnv,
    convertToFilterSelectedValues: () => convertToFilterSelectedValues(tokenList.value),
    isFilterActive,
    persistActiveState: props.persistActiveState ?? false,
    filterHistory,
    pinnedTokenList,
  });

  // Dropdown
  const {
    isShowDropdown,
    dropdownSearchText,
    dropdownListInfo,
    dropdownListRef,
    filterMainRef,
    hoveredDropdownItemIndex,
    isKeyboardNavigating,
    initHoveredDropdownItemIndex,
    showDropdownList,
    hideDropdownList,
    isSelectedDropdownItem,
    setKeyDropdownList,
    setOperatorDropdownList,
    setValueDropdownList,
    setMultiSelectedValues,
    filterByInput,
    setDropdownListByLastToken,
    scrollSelectedItemIntoView,
  } = useFilterDropdown({
    filterItems,
    editingToken,
    lastToken,
    autoValueItems: computed(() => props.autoValueItems),
  });

  const syncActiveToSessionStore = () => {
    const slideValue = getCurrentSlideValue();
    if (slideValue == null) return;
    filterActiveSessionStore.setActive(slideValue, props.storageKey, isFilterActive.value);
  };

  const activateFilter = async () => {
    isFilterActive.value = true;
    await saveFilterActiveState();
    syncActiveToSessionStore();
    emitComplete();
  };

  const deactivateFilter = async () => {
    isFilterActive.value = false;
    await saveFilterActiveState();
    syncActiveToSessionStore();
    emitComplete();
  };

  // Layout
  const {
    filterSearchRef,
    tokensMoreBtnWrapperRef,
    tokensWrapperRef,
    mainInputRef,
    mainInputValue,
    moreCount,
    isExpand,
    isFocus,
    tokenRefs,
    setElementRef,
    focusMainInput,
    setMoreCount,
    expand,
    onClickMoreCount,
    focusPartsInput,
  } = useFilterLayout({
    tokenList,
    editingToken,
    isFilterActive,
    activateFilter,
    dropdownListInfo,
    hideDropdownList,
    quitEditingToken: () => quitEditingToken(),
  });

  // History
  const { isShowFilterHistoryList, saveHistory, removeHistoryItem } = useFilterHistory({
    filterHistory,
    tokenList,
    editingToken,
    lastToken,
    saveHistoryToEnv,
  });

  // Tokens
  const {
    convertToFilterSelectedValues,
    removeTokens,
    removeToken,
    isActiveAddMultiValueBtn,
    createDraftToken,
    setTokenParts,
    quitEditingToken,
    onClickResumeToken,
    onClickAddMultiValue,
    setTokenListBySelectedResult,
    setMainInputValueReset,
  } = useFilterTokens({
    filterItems,
    tokenList,
    editingToken,
    lastToken,
    selectedLogicalOperator,
    dropdownListInfo,
    tokenRefs,
    moreCount,
    setKeyDropdownList,
    setOperatorDropdownList,
    setValueDropdownList,
    setMultiSelectedValues,
    isSelectedDropdownItem,
    initHoveredDropdownItemIndex,
    showDropdownList,
    hideDropdownList,
    focusPartsInput,
    setMoreCount,
    saveHistory,
    getEmitComplete,
  });

  // mainInputValue 리셋 콜백 연결
  setMainInputValueReset(() => {
    mainInputValue.value = '';
  });

  const lastEmittedResult = ref<FilterSelectedValues | null>(null);

  emitComplete = () => {
    const result = isFilterActive.value
      ? convertToFilterSelectedValues(tokenList.value)
      : { logicalOperator: selectedLogicalOperator.value, tokens: [] };

    if (isEqual(result, lastEmittedResult.value)) {
      return;
    }

    lastEmittedResult.value = cloneDeep(result);
    emit('complete', result);
    emit('update:selectedValues', result);
  };

  // Keyboard
  const {
    onCompositionStart,
    onCompositionEnd,
    onInput,
    onKeyUpDown,
    addTokenByHistory,
    onKeyEnter,
    onKeyCtrlEnter,
    onKeyBackspace,
    onKeyEsc,
    onTokenBackspace,
    onFocusMainInput,
    onClickOutside,
    onWheelOutside,
  } = useFilterKeyboard({
    filterItems,
    tokenList,
    editingToken,
    lastToken,
    isFilterActive,
    dropdownListInfo,
    isShowDropdown,
    hoveredDropdownItemIndex,
    isKeyboardNavigating,
    filterHistory,
    isShowFilterHistoryList,
    mainInputRef,
    mainInputValue,
    isFocus,
    isExpand,
    tokenRefs,
    activateFilter,
    initHoveredDropdownItemIndex,
    showDropdownList,
    hideDropdownList,
    filterByInput,
    setKeyDropdownList,
    setOperatorDropdownList,
    setDropdownListByLastToken,
    scrollSelectedItemIntoView,
    focusMainInput,
    focusPartsInput,
    setMoreCount,
    setTokenParts,
    createDraftToken,
    quitEditingToken,
    onClickResumeToken,
    saveHistory,
    getEmitComplete,
  });

  /**
   * Cross-cutting: event handlers
   */
  const onClickDropdownItem = async (selectedDropdownItem: FilterDropdownItem) => {
    const prevEditingStatus = editingToken.value?.status;
    const isEditingExistingToken = prevEditingStatus === TOKEN_STATUS.VALUE_DONE;

    await setTokenParts(selectedDropdownItem);

    if (!editingToken.value && (!dropdownListInfo.multi || !dropdownListInfo.items?.length)) {
      if (isEditingExistingToken) {
        mainInputRef.value?.blur();
      } else {
        await focusMainInput();
      }
    } else if (editingToken.value && dropdownListInfo.multi && dropdownListInfo.items?.length) {
      focusPartsInput(editingToken.value.id);
    }
  };

  const onClickTokenParts = async ({
    targetToken,
    category,
  }: {
    targetToken: FilterToken;
    category: FilterDropdownInfoCategories;
  }) => {
    if (!isFilterActive.value) {
      await activateFilter();
    }

    initHoveredDropdownItemIndex();

    if (editingToken.value) {
      await quitEditingToken();
    }

    editingToken.value = {
      ...targetToken,
      backupValues: cloneDeep(targetToken.values),
    };

    const targetTokenKey = editingToken.value.key.id;
    if (category === 'operator') {
      setOperatorDropdownList(targetTokenKey);

      const targetFilterItemValuesInfo = filterItems.value.find(
        (item) => item.key.id === targetToken.key.id,
      )?.values;
      if (targetFilterItemValuesInfo?.multi && targetToken.values?.length) {
        const hasListValue = targetToken.values?.some((value) =>
          targetFilterItemValuesInfo?.items?.some((item) => item.id === value.id),
        );

        if (hasListValue) {
          dropdownListInfo.filteredItems.map((item) => {
            if (item.id === 'like' || item.id === 'not like') {
              item.disabled = true;
            }
            return item;
          });
        } else {
          dropdownListInfo.filteredItems.map((item) => {
            item.disabled = item.id !== 'like' && item.id !== 'not like';
            return item;
          });
        }
      }
    } else if (category === 'value') {
      setValueDropdownList(
        targetTokenKey,
        () =>
          editingToken.value?.operator?.id !== 'like' &&
          editingToken.value?.operator?.id !== 'not like',
        (a: FilterDropdownItem, b: FilterDropdownItem) => {
          if (a.checked && !b.checked) {
            return -1;
          }
          if (!a.checked && b.checked) {
            return 1;
          }
          return 0;
        },
      );
    }

    dropdownListInfo.isEditing = true;
    showDropdownList();

    await nextTick();
    focusPartsInput(targetToken.id);
  };

  const onClickRemoveToken = async (targetTokenIndex: number, isEditingToken: boolean) => {
    if (!isFilterActive.value) {
      await activateFilter();
    }
    await removeToken(targetTokenIndex);

    if (isEditingToken) {
      await quitEditingToken();
      await focusMainInput();
    } else if (isFocus.value) {
      setDropdownListByLastToken();
    }

    await setMoreCount();
    emitComplete();
  };

  const onClickClearAll = async () => {
    if (isExpand.value) {
      isExpand.value = false;
      isFocus.value = false;
      hideDropdownList();
    }

    removeTokens();
    await removeFilterSelectedValues();
    await deactivateFilter();
  };

  // ModernGrid context 내 외부 FilterSearch 자동 등록
  const registry = inject(FILTER_SEARCH_REGISTRY_KEY, null);
  if (registry) {
    const self = { clearFilterSearchToken: onClickClearAll };
    registry.register(self);
    onBeforeUnmount(() => registry.unregister(self));
  }

  const onClickPowerButton = async () => {
    if (isFilterActive.value) {
      await deactivateFilter();
    } else {
      await activateFilter();
    }
  };

  /**
   * Logical Operator
   */
  const logicalOperatorListItems = [
    {
      name: t('WORD.SEARCH_BY'),
      value: t('WORD.SEARCH_BY'),
      disabled: true,
    },
    ...LOGICAL_OPERATORS,
  ];

  const onChangeLogicalOperator = async () => {
    if (!isFilterActive.value) {
      await activateFilter();
    }
    await saveFilterSelectedValues();
    emitComplete();
  };

  /**
   * Dropdown position watcher
   */
  const MARGIN_FROM_PARENT = 3;
  const dropdownListWrapperStyle = ref<{
    top: string;
    width: string;
    marginTop: string;
  }>({
    top: '',
    width: 'auto',
    marginTop: `${MARGIN_FROM_PARENT}px`,
  });

  const filterMainSize = ref({ width: 0, height: 0 });
  const filterMainRo = new ResizeObserver((entries) => {
    const entry = entries[0];
    if (entry) {
      filterMainSize.value = {
        width: entry.contentRect.width,
        height: entry.contentRect.height,
      };
    }
  });

  watch(
    () => [
      isShowDropdown.value,
      tokenList.value?.length,
      dropdownListInfo.filteredItems?.length,
      filterMainSize.value.height,
    ],
    async () => {
      const style = {
        top: '',
        width: 'auto',
        marginTop: `${MARGIN_FROM_PARENT}px`,
      };

      await nextTick();

      const parentEl = filterMainRef.value;
      if (parentEl && isShowDropdown.value) {
        const parentRect = parentEl.getBoundingClientRect();
        const documentEl = document.documentElement;

        style.width = `${parentRect.width}px`;

        const distanceFromBottom =
          documentEl.scrollHeight + documentEl.scrollTop - parentRect.bottom;
        const listHeight = dropdownListRef.value?.clientHeight ?? 0;

        if (listHeight > distanceFromBottom) {
          style.top = `${parentRect.top - listHeight}px`;
          style.marginTop = `${-MARGIN_FROM_PARENT}px`;
        } else {
          style.top = `${parentRect.bottom}px`;
          style.marginTop = `${MARGIN_FROM_PARENT}px`;
        }

        // note: 뷰포트 기준으로 포지션 지정하지 않기 위함
        const hasScrollParent = parentEl.closest('.filter-search-parent');
        if (hasScrollParent) {
          style.top = 'auto';
        }
      }

      dropdownListWrapperStyle.value = style;
    },
  );

  /**
   * Watchers
   */
  watch(
    tokenList,
    async () => {
      if (isSuppressingSave.value) {
        return;
      }
      const current = convertToFilterSelectedValues(tokenList.value);
      if (isEqual(current, lastSavedFilterResult.value)) {
        return;
      }
      lastSavedFilterResult.value = cloneDeep(current);
      await saveFilterSelectedValues();
    },
    { deep: true },
  );

  watch(isShowDropdown, (showList) => {
    if (!showList && tokenList.value?.length) {
      emitComplete();
    }
  });

  watch(
    () => lastToken?.value?.status,
    async (value) => {
      if (value === TOKEN_STATUS.VALUE_DONE && lastToken?.value?.values?.length) {
        await saveHistory(lastToken!.value!);
        emitComplete();
      }
    },
  );

  /**
   * Resize
   */
  const ro = new ResizeObserver(async () => {
    await nextTick();
    await setMoreCount();
  });

  /**
   * Lifecycle
   */
  onMounted(async () => {
    await loadEnvData();
    const slideValue = getCurrentSlideValue();
    if (slideValue != null) {
      const savedActive = filterActiveSessionStore.getActive(slideValue, props.storageKey);
      if (savedActive != null) {
        isFilterActive.value = savedActive;
      }
    }
    setTokenListBySelectedResult(props.selectedValues ?? pinnedTokenList.value);
    await setMoreCount();
    // why: async onMounted 의 await 중 컴포넌트가 언마운트되면 (예: ModernGrid :key 변경 remount)
    // 템플릿 ref 가 null 로 초기화되어 observe 가 TypeError 를 던진다.
    if (filterSearchRef.value) {
      ro.observe(filterSearchRef.value);
    }
    if (filterMainRef.value) {
      filterMainRo.observe(filterMainRef.value);
    }
  });

  watch(
    () => route.fullPath,
    () => {
      if (props.persistActiveState) return;
      if (slideStore) return;
      isFilterActive.value = props.defaultActive ?? false;
    },
  );

  onBeforeUnmount(() => {
    ro.disconnect();
    filterMainRo.disconnect();
  });

  watch(
    () => props.filterItems,
    async (newItems, oldItems) => {
      const newKeys = sortBy(
        newItems
          .map((item) => item.key.id)
          .filter((key) => (props.useLogFilter ? !key.startsWith(`${props.useLogFilter}.`) : true)),
      );
      const oldKeys = sortBy(
        oldItems
          .map((item) => item.key.id)
          .filter((key) => (props.useLogFilter ? !key.startsWith(`${props.useLogFilter}.`) : true)),
      );

      if (newKeys.length && !isEqual(newKeys, oldKeys)) {
        const currentVersion = ++filterItemsWatcherVersion;
        filterItems.value = cloneDeep(props.filterItems);
        isSuppressingSave.value = true;
        removeTokens();
        const emptyResult: FilterSelectedValues = {
          logicalOperator: selectedLogicalOperator.value,
          tokens: [],
        };
        lastEmittedResult.value = cloneDeep(emptyResult);
        emit('update:selectedValues', emptyResult);
        await loadEnvData();

        if (currentVersion !== filterItemsWatcherVersion) {
          return;
        }

        isSuppressingSave.value = false;
        setTokenListBySelectedResult(pinnedTokenList.value);
      } else if (newKeys.length && !isEqual(newItems, oldItems)) {
        filterItems.value = cloneDeep(props.filterItems);
      }
    },
  );

  return {
    t,
    logicalOperatorListItems,
    selectedLogicalOperator,
    onChangeLogicalOperator,

    isFilterActive,
    onClickPowerButton,

    isFocus,
    filterSearchRef,
    tokensMoreBtnWrapperRef,
    tokensWrapperRef,
    setElementRef,
    moreCount,
    isExpand,
    expand,
    onClickOutside,
    onWheelOutside,

    isShowDropdown,
    dropdownListInfo,
    dropdownSearchText,
    hoveredDropdownItemIndex,
    isKeyboardNavigating,
    dropdownListRef,
    filterMainRef,
    dropdownListWrapperStyle,
    initHoveredDropdownItemIndex,
    onClickDropdownItem,

    mainInputRef,
    mainInputValue,
    onCompositionStart,
    onCompositionEnd,
    onInput,
    onKeyEnter,
    onKeyCtrlEnter,
    onKeyUpDown,
    onKeyBackspace,
    onKeyEsc,
    onFocusMainInput,

    tokenList,
    editingToken,
    lastToken,
    hasCompletedToken,
    onClickRemoveToken,
    onClickResumeToken,
    onClickTokenParts,
    isActiveAddMultiValueBtn,
    onClickAddMultiValue,
    onClickClearAll,
    onTokenBackspace,

    filterHistory,
    isShowFilterHistoryList,
    addTokenByHistory,
    removeHistoryItem,
    onClickMoreCount,
  };
};
